import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Chess, DEFAULT_POSITION} from "chess.js";
import ChessBoard from "./ChessBoard";
import EvalBar, {formatEval} from "./EvalBar";
import CustomRange from "./CustomRange";
import Icon from "./ui/Icon";
import loadRandomPosition, {loadShuffledBoard} from "../clients/GameClient";
import {LEVELS, levelInfo} from "../utils/engine";
import {getSharedEngine} from "../utils/sharedEngine";
import LinePreviewBar from "./rook/LinePreviewBar";
import {playMoveSound} from "../utils/sounds";

const STORAGE_KEY = "chesscorner.practiceBoard";

const readSettings = () => {
    try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
        if (saved) return {evalOn: !!saved.evalOn, level: levelInfo(saved.level).level};
    } catch (e) {
        // ignore
    }
    return {evalOn: false, level: 3};
};

const saveSettings = (settings) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
        // ignore
    }
};

const toUci = (move) => move.from + move.to + (move.promotion || "");

// Replays the moves so chess.js knows the history (needed for repetition draws)
const replay = (startFen, moves) => {
    const chess = new Chess(startFen);
    moves.forEach((m) => chess.move(m.san));
    return chess;
};

const statusText = (chess) => {
    if (chess.isCheckmate()) return `Checkmate. ${chess.turn() === "w" ? "Black" : "White"} wins.`;
    if (chess.isStalemate()) return "Stalemate. It's a draw.";
    if (chess.isThreefoldRepetition()) return "Draw by repetition.";
    if (chess.isInsufficientMaterial()) return "Draw. Neither side can checkmate.";
    if (chess.isDraw()) return "Draw by the 50 move rule.";
    return null;
};

/**
 * The practice board on the front page: move both sides freely, load positions, and use the computer (the Stockfish
 * that runs in the browser) to evaluate the position, make a move, or answer your moves automatically.
 */
const PracticeBoard = ({preview, onPositionChange}) => {
    const [orientation, setOrientation] = useState("white");
    // The position the board started from, and every move made since (so we can undo, and so the computer knows the
    // history, for example to avoid or aim for repetitions)
    const [history, setHistory] = useState({startFen: DEFAULT_POSITION, moves: []});
    const [showRules, setShowRules] = useState(false);
    const [evalRange, setEvalRange] = useState([-5, 5]);
    const [moveRange, setMoveRange] = useState([0, 100]);
    const [loadingPosition, setLoadingPosition] = useState(false);
    const [positionError, setPositionError] = useState(null);

    const [settings, setSettings] = useState(readSettings);
    const [computerSide, setComputerSide] = useState(null);   // null, "w" or "b": the computer answers for that side
    const [thinking, setThinking] = useState(false);
    const [evaluation, setEvaluation] = useState(null);       // {fen, eval, best: {from, to, san}}
    const [engineError, setEngineError] = useState(null);

    // The small menu with the computer's level and side
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);
    useEffect(() => {
        if (!menuOpen) return undefined;
        const onDown = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) setMenuOpen(false);
        };
        const onKey = (event) => event.key === "Escape" && setMenuOpen(false);
        document.addEventListener("pointerdown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("pointerdown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [menuOpen]);

    useEffect(() => saveSettings(settings), [settings]);

    const fen = history.moves.length ? history.moves[history.moves.length - 1].fen : history.startFen;
    const lastMove = history.moves.length ? history.moves[history.moves.length - 1] : null;
    const chess = useMemo(() => replay(history.startFen, history.moves), [history]);
    const turn = chess.turn();
    const status = statusText(chess);
    const isOver = !!status;

    // The engine is only downloaded and started the first time the computer (or Rook) is used. It is shared with
    // Rook, so it keeps running when you leave the page.
    const getEngine = getSharedEngine;

    // Rook needs to know the position on this board
    useEffect(() => {
        if (onPositionChange) onPositionChange({startFen: history.startFen, moves: history.moves.map((m) => m.uci)});
    }, [history, onPositionChange]);
    const previewing = !!preview?.active;

    const historyRef = useRef(history);
    historyRef.current = history;

    const enginePosition = (h) => ({startFen: h.startFen, moves: h.moves.map((m) => m.uci)});

    const pushMove = useCallback((move, newFen) => {
        setHistory((h) => ({...h, moves: [...h.moves, {san: move.san, uci: toUci(move), fen: newFen, from: move.from, to: move.to}]}));
        const after = new Chess(newFen);
        playMoveSound(move, after.inCheck());
    }, []);

    const startFrom = (startFen) => {
        setHistory({startFen, moves: []});
        setEvaluation(null);
        setThinking(false);
    };

    // ----- The computer makes a move -----
    const computerMove = useCallback(async () => {
        const h = historyRef.current;
        const current = replay(h.startFen, h.moves);
        if (statusText(current)) return;
        const fenBefore = current.fen();
        const level = levelInfo(settings.level);
        setThinking(true);
        setEngineError(null);
        try {
            let move;
            const legal = current.moves({verbose: true});
            if (level.randomness && Math.random() < level.randomness) {
                move = legal[Math.floor(Math.random() * legal.length)];
            } else {
                const uci = await getEngine().bestMove(enginePosition(h), level);
                if (!uci) return;
                move = legal.find((m) => toUci(m) === uci || (m.from + m.to === uci.slice(0, 4) && !uci[4]));
            }
            // The board changed while the computer was thinking (undo, reset...): throw the answer away
            const now = historyRef.current;
            const nowFen = now.moves.length ? now.moves[now.moves.length - 1].fen : now.startFen;
            if (!move || nowFen !== fenBefore) return;
            const working = new Chess(fenBefore);
            const played = working.move({from: move.from, to: move.to, promotion: move.promotion});
            pushMove(played, working.fen());
        } catch (e) {
            if (!e.stopped) setEngineError("The computer could not answer. Try again.");
        } finally {
            setThinking(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings.level, pushMove]);

    // "Computer plays white/black": answer automatically whenever it is that side's turn
    useEffect(() => {
        if (!computerSide || isOver || thinking || turn !== computerSide || previewing) return;
        const timer = setTimeout(computerMove, 350);
        return () => clearTimeout(timer);
    }, [computerSide, isOver, thinking, turn, fen, computerMove, previewing]);

    // ----- Evaluation bar and best-move arrow -----
    useEffect(() => {
        if (!settings.evalOn) return;
        if (isOver) {
            setEvaluation({fen, eval: chess.isCheckmate() ? (turn === "w" ? "mate -0" : "mate 0") : 0, best: null});
            return;
        }
        let cancelled = false;
        const timer = setTimeout(() => {
            getEngine().analyse(enginePosition(historyRef.current), {depth: 16, movetime: 700})
                .then((result) => {
                    if (cancelled) return;
                    // The engine reports from the side to move; the bar shows white's point of view
                    const sign = turn === "w" ? 1 : -1;
                    const value = result.mate !== null
                        ? `mate ${sign * result.mate < 0 ? "-" : ""}${Math.abs(result.mate)}`
                        : (sign * (result.cp || 0)) / 100;
                    let best = null;
                    if (result.pv.length) {
                        try {
                            const m = new Chess(fen).move({from: result.pv[0].slice(0, 2), to: result.pv[0].slice(2, 4), promotion: result.pv[0][4]});
                            best = {from: m.from, to: m.to, san: m.san};
                        } catch (e) {
                            best = null;
                        }
                    }
                    setEvaluation({fen, eval: value, best});
                })
                .catch((e) => {
                    if (!cancelled && !e.stopped) setEngineError("The evaluation is not available right now.");
                });
        }, 150);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings.evalOn, fen]);

    const shownEvaluation = settings.evalOn && evaluation && evaluation.fen === fen ? evaluation : null;

    // ----- Buttons -----
    const undo = () => {
        setHistory((h) => {
            let moves = h.moves.slice(0, -1);
            // When the computer answers for one side, go back to a position where it is your turn again
            if (computerSide && moves.length) {
                const after = replay(h.startFen, moves);
                if (after.turn() === computerSide) moves = moves.slice(0, -1);
            }
            return {...h, moves};
        });
    };

    const loadPosition = (promise) => {
        setLoadingPosition(true);
        setPositionError(null);
        promise
            .then((data) => {
                if (data && data.fen) startFrom(data.fen);
                else setPositionError("Could not find a position. Try different settings.");
            })
            .catch(() => setPositionError("Could not load a position right now."))
            .finally(() => setLoadingPosition(false));
    };

    const shuffleBoard = () => {
        if (showRules) loadPosition(loadRandomPosition(evalRange[0], evalRange[1], moveRange[0], moveRange[1]));
        else loadPosition(loadShuffledBoard());
    };

    const level = levelInfo(settings.level);
    const arrows = shownEvaluation?.best ? [[shownEvaluation.best.from, shownEvaluation.best.to, "rgba(95, 157, 178, 0.85)"]] : [];
    const playerColor = computerSide ? (computerSide === "w" ? "b" : "w") : undefined;

    let note = null;
    if (status) note = status;
    else if (thinking) note = "The computer is thinking…";
    else if (engineError) note = engineError;
    else if (settings.evalOn) {
        note = shownEvaluation
            ? (shownEvaluation.best ? `Best: ${shownEvaluation.best.san} (${formatEval(shownEvaluation.eval) || "0.0"})` : `Evaluation: ${formatEval(shownEvaluation.eval) || "0.0"}`)
            : "Evaluating…";
    }
    const sideName = computerSide === "w" ? "White" : computerSide === "b" ? "Black" : null;

    return (
        <div className="card">
            <div className="card-header">
                <div>
                    <div className="card-title"><Icon name="board"/> Practice board</div>
                    {/* The best move, the computer thinking, or the result, shown where it doesn't take a row of its own */}
                    {note
                        ? <div className={`card-subtitle practice-note ${status ? "practice-note--status" : ""}`} aria-live="polite">{note}</div>
                        : <div className="card-subtitle hide-sm">Move both sides freely, or let the computer help</div>}
                </div>
                <div className="sandbox-toolbar">
                    <button className="icon-btn" onClick={shuffleBoard} disabled={loadingPosition || previewing}
                            aria-label="Load a random position" title={showRules ? "Random position from a real game" : "Shuffle the back rank (Chess960 style)"}>
                        {loadingPosition ? <span className="spinner spinner--sm"/> : <Icon name="shuffle"/>}
                    </button>
                    <button className={`icon-btn ${showRules ? "active" : ""}`} onClick={() => setShowRules((prev) => !prev)}
                            aria-pressed={showRules} aria-label="Randomisation settings" title="Use positions from real games">
                        <Icon name="sliders"/>
                    </button>
                    <button className="icon-btn" onClick={() => setOrientation((o) => (o === "white" ? "black" : "white"))}
                            aria-label="Flip board" title="Flip board">
                        <Icon name="flip"/>
                    </button>
                    <button className="icon-btn" onClick={() => startFrom(DEFAULT_POSITION)} disabled={previewing} aria-label="Reset board" title="Reset">
                        <Icon name="reset"/>
                    </button>
                </div>
            </div>
            <div className="card-body">
                <div className="board-with-eval" style={{maxWidth: "min(100%, 76vh)", margin: "0 auto"}}>
                    {settings.evalOn && <EvalBar evaluation={shownEvaluation ? shownEvaluation.eval : null} orientation={orientation}
                                                 disabled={!shownEvaluation}/>}
                    <ChessBoard id="practice-board" fen={previewing ? preview.fen : fen} orientation={orientation} onMove={pushMove}
                                lastMove={previewing ? preview.lastMove : (lastMove ? {from: lastMove.from, to: lastMove.to} : null)}
                                canMove={!previewing && !isOver && (!thinking || !!computerSide)}
                                locked={previewing} previewing={previewing}
                                annotations={preview?.annotations}
                                playerColor={playerColor} premoves={!!computerSide} arrows={previewing ? [] : arrows}/>
                </div>
                {previewing ? <LinePreviewBar preview={preview}/> : (
                    <div className="practice-bar">
                        <button className="btn btn-sm" onClick={undo} disabled={!history.moves.length} title="Take back the last move">
                            <Icon name="undo"/> <span className="label-icon-hide">Undo</span>
                        </button>
                        <button className={`btn btn-sm ${settings.evalOn ? "btn-toggle-on" : ""}`} aria-pressed={settings.evalOn}
                                onClick={() => setSettings((s) => ({...s, evalOn: !s.evalOn}))}
                                title="Show Stockfish's evaluation and best move">
                            <Icon name="sparkles"/> <span className="label-long">Evaluation</span><span className="label-short">Eval</span>
                        </button>
                        <button className="btn btn-sm" onClick={computerMove} disabled={isOver || thinking}
                                title={`Let the computer (level ${level.level}) make the next move`}>
                            {thinking ? <span className="spinner spinner--sm"/> : <Icon name="cpu"/>}
                            <span className="label-long">Computer move</span><span className="label-short">Move</span>
                        </button>
                        <div className="practice-menu" ref={menuRef}>
                            <button className={`btn btn-sm ${computerSide ? "btn-toggle-on" : ""}`} aria-expanded={menuOpen}
                                    aria-haspopup="dialog" onClick={() => setMenuOpen((o) => !o)}
                                    title="The computer's level, and whether it answers your moves">
                                <Icon name="sliders"/>
                                <span className="label-long">Level {level.level}{sideName ? ` · ${sideName}` : ""}</span>
                                <span className="label-short">Lv {level.level}<span className="label-side">{sideName ? ` · ${sideName}` : ""}</span></span>
                                <Icon name="chevronDown" size={14}/>
                            </button>
                            {menuOpen && (
                                <div className="practice-menu__panel" role="dialog" aria-label="Computer settings">
                                    <label className="practice-menu__field">
                                        <span>Computer level</span>
                                        <select className="select select--sm" value={settings.level}
                                                onChange={(e) => setSettings((s) => ({...s, level: Number(e.target.value)}))}>
                                            {LEVELS.map((l) => <option key={l.level} value={l.level}>{l.level} · {l.name}</option>)}
                                        </select>
                                    </label>
                                    <div className="practice-menu__field">
                                        <span>Computer plays</span>
                                        <div className="segmented segmented--sm" role="group" aria-label="Computer plays">
                                            {[[null, "Off"], ["w", "White"], ["b", "Black"]].map(([side, label]) => (
                                                <button key={label} type="button" className={computerSide === side ? "active" : ""}
                                                        aria-pressed={computerSide === side}
                                                        onClick={() => {
                                                            setComputerSide(side);
                                                            if (side) setOrientation(side === "w" ? "black" : "white");
                                                        }}>
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <p className="small muted">When the computer plays a side, it answers your moves by itself.</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {positionError && <div className="form-error" style={{marginTop: "0.75rem"}}>{positionError}</div>}
            </div>

            {showRules && (
                <div className="sandbox-rules">
                    <CustomRange title="Evaluation (pawns, + is white)" min={-5} max={5} step={0.5}
                                 initial={evalRange} onChange={setEvalRange}/>
                    <CustomRange title="Moves into the game" min={0} max={100} step={5}
                                 initial={moveRange} onChange={setMoveRange}/>
                </div>
            )}
        </div>
    );
};

export default PracticeBoard;
